import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/authSelectors";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { User, CreateUserData, EditUserData } from "../../store/types";
import { getErrorMessage } from "./use-getError";

export const useUsersAPI = () => {
  const isAdmin = useAppSelector(selectIsAdmin);
  const usersEP = useAppSelector((state) => state.endpoints.users);
  const meEP = useAppSelector((state) => state.endpoints.me);
  const activateEP = useAppSelector((state) => state.endpoints.activateUser);

  const [users, setUsers] = useState<User[] | null>(null);
  const { toastRef, showSuccess, showWarning, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const { data: fetchedUsers, refetch: refetchUsers } = useGetQuery(usersEP, {
    refetchOnMountOrArgChange: true,
    skip: !usersEP || !isAdmin,
  });

  const refetchData = async () => {
    await refetchUsers();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const handlePostUser = async (
    user: CreateUserData
  ): Promise<{ success: boolean; user?: User; token?: string }> => {
    try {
      const result = await addItem({
        endpoint: usersEP,
        id: "",
        body: user,
      });

      if (result.data) {
        const { token, user: newUser } = result.data;
        if (token) localStorage.setItem("token", token);
        if (newUser) localStorage.setItem("user", JSON.stringify(newUser));

        showSuccess("Успешно", "Пользователь создан");
        return { success: true, user: newUser, token };
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("создать пользователя", serverError ?? result.error);
      return { success: false };
    } catch (error) {
      handleError("создать пользователя", error);
      return { success: false };
    }
  };

  const handleActivateUser = async (
    password: string,
    token: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: activateEP,
        id: "",
        body: {
          userId,
          token,
          password,
        },
      });

      if (!result.error) {
        showSuccess("Успешно", "Пользователь активирован");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("активировать пользователя", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("активировать пользователя", error);
      return false;
    }
  };

  const handleUpdateUser = async (user: EditUserData): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: usersEP,
        id: user?.id,
        body: user,
      });

      if (result.data) {
        showSuccess("Успешно", "Данные пользователя обновлены");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("обновить пользователя", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("обновить пользователя", error);
      return false;
    }
  };

  const handleDeleteUser = async (user: User): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: usersEP,
        id: user?.id,
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError("удалить пользователя", serverError ?? result.error);
        return false;
      }

      showSuccess("Успешно", "Пользователь удалён");
      return true;
    } catch (error) {
      handleError("удалить пользователя", error);
      return false;
    }
  };

  const handleSendActivationLink = async (userId: string): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: `${usersEP}/${userId}/send-activation-link`,
        id: "",
      });
      if (result.error) {
        showError("Ошибка", "Не удалось отправить ссылку активации");
        return false;
      }
      showSuccess(
        "Успешно",
        "Ссылка активации отправлена на email пользователя"
      );
      return true;
    } catch {
      showError("Ошибка", "Не удалось отправить ссылку активации");
      return false;
    }
  };

  const handleSendResetPasswordLink = async (
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: `${usersEP}/${userId}/send-password-reset-link`,
        id: "",
      });
      if (result.error) {
        showError("Ошибка", "Не удалось отправить ссылку на сброс пароля");
        return false;
      }
      showSuccess(
        "Успешно",
        "Ссылка на сброс пароля отправлена на email пользователя"
      );
      return true;
    } catch {
      showError("Ошибка", "Не удалось отправить ссылку на сброс пароля");
      return false;
    }
  };

  const handleResetPassword = async (
    password: string,
    token: string,
    userId: string
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: `${usersEP}/${userId}/password`,
        id: "",
        body: {
          token,
          password,
        },
      });

      if (!result.error) {
        showSuccess("Успешно", "Новый пароль сохранен");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("сохранить новый пароль", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("сохранить новый пароль", error);
      return false;
    }
  };

  useEffect(() => {
    if (fetchedUsers) setUsers(fetchedUsers);
  }, [fetchedUsers]);

  return {
    usersEP,
    meEP,
    handlePostUser,
    handleActivateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleSendActivationLink,
    handleSendResetPasswordLink,
    handleResetPassword,
    refetchData,
    users: users as User[] | null,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
  };
};
